import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle2, UploadCloud, X, Plus, Trash2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Field, inputProps } from "@/components/Field";

const DRAFT_KEY = "nb_application_draft_v2";

const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const blankWeek = () => Object.fromEntries(WEEK_DAYS.map((d) => [d, { am: false, pm: false }]));

const initialData = {
  legal_name: "", address: "", city: "", state: "NJ", zip: "", phone: "", email: "", preferred_contact: "",
  positions: [], other_position: "", hear_about: "", salary_desired: "", start_date: "",
  availability: { types: [], shifts: [], details: "", week: blankWeek(), overtime: "" },
  service_areas: "", reliable_transportation: "", drivers_license: "",
  is_18: "", work_authorized: "", proof_document: "", convicted: "", conviction_details: "",
  skills: "",
  education: [
    { level: "High School", school: "", year: "", degree: "" },
    { level: "University", school: "", year: "", degree: "" },
    { level: "Trade School", school: "", year: "", degree: "" },
  ],
  military: { branch: "", rank: "", years: "", skills: "" },
  employment: [{ employer: "", job_title: "", supervisor: "", employer_address: "", employer_city_state_zip: "", employer_phone: "", from: "", to: "", reason_leaving: "" }],
  licenses: [{ type: "", number: "", issuing_authority: "", expiration: "" }],
  malpractice_policy: "", malpractice_carrier: "", malpractice_carrier_address: "",
  references: [
    { name: "", relationship: "", company: "", phone: "", email: "" },
    { name: "", relationship: "", company: "", phone: "", email: "" },
    { name: "", relationship: "", company: "", phone: "", email: "" },
  ],
  certify_true: false, ack_authorization: false,
  signature_name: "", signature_date: new Date().toISOString().slice(0, 10),
};

const STEPS = [
  "Personal Information", "Position", "Availability", "Service Area",
  "Education & Employment", "Licenses & Certifications", "References",
  "Document Upload", "Review & Sign",
];

const POSITIONS = ["Home Health Aide (HHA)", "Certified Home Health Aide (CHHA)", "CNA", "LPN", "RN", "Medical Assistant", "DSP", "Other"];
const AVAIL_TYPES = ["Full-time", "Part-time", "Per diem"];
const AVAIL_SHIFTS = ["Weekdays", "Weekends", "Days", "Evenings", "Overnight", "Live-in"];
const LICENSE_TYPES = ["RN", "LPN", "HHA", "NA (Nursing Assistant)", "Other Professional Certification"];
const DOC_SLOTS = [
  { key: "resume", label: "Resume" },
  { key: "license_doc", label: "Professional License / Certification" },
  { key: "cpr_card", label: "CPR / BLS Card" },
  { key: "other_doc", label: "Other Employment Credential" },
];

const Chips = ({ options, values, onChange, testid }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((o) => {
      const active = values.includes(o);
      return (
        <button
          type="button"
          key={o}
          data-testid={`${testid}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
          onClick={() => onChange(active ? values.filter((v) => v !== o) : [...values, o])}
          className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-200 ${
            active ? "border-gold bg-gold-soft text-gold-deep" : "border-blush-300/70 bg-white text-charcoal-muted hover:border-blush-400"
          }`}
        >
          {o}
        </button>
      );
    })}
  </div>
);

const Err = ({ k, errors }) =>
  errors[k] ? <p className="mt-1.5 text-xs font-medium text-red-600">{errors[k]}</p> : null;

const DayGrid = ({ week, onChange }) => (
  <div className="overflow-hidden rounded-2xl border border-blush-200">
    <div className="grid grid-cols-[1fr_64px_64px] bg-blush-100/70 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-charcoal-muted sm:grid-cols-[1fr_88px_88px]">
      <span className="px-4 py-2.5">Day</span>
      <span className="px-2 py-2.5 text-center">AM</span>
      <span className="px-2 py-2.5 text-center">PM</span>
    </div>
    {WEEK_DAYS.map((day) => (
      <div key={day} className="grid grid-cols-[1fr_64px_64px] items-center border-t border-blush-100 sm:grid-cols-[1fr_88px_88px]">
        <span className="px-4 py-2.5 text-sm text-charcoal-light">{day}</span>
        {["am", "pm"].map((slot) => (
          <button
            key={slot}
            type="button"
            data-testid={`apply-week-${day.toLowerCase()}-${slot}`}
            onClick={() => onChange({ ...week, [day]: { ...week[day], [slot]: !week[day][slot] } })}
            className={`mx-auto my-1.5 flex h-8 w-8 items-center justify-center rounded-lg border transition-colors duration-200 ${
              week[day][slot] ? "border-gold bg-gold text-white" : "border-blush-300 bg-white hover:border-gold/50"
            }`}
            aria-label={`${day} ${slot.toUpperCase()}`}
          >
            {week[day][slot] && <CheckCircle2 className="h-4 w-4" />}
          </button>
        ))}
      </div>
    ))}
  </div>
);

const FileSlot = ({ slot, file, onPick, onClear }) => (
  <div className="rounded-2xl border border-blush-200 bg-white p-5">
    <p className="text-sm font-semibold text-charcoal">{slot.label}</p>
    {file ? (
      <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-gold/30 bg-gold-soft/50 px-4 py-3">
        <span className="truncate text-sm text-charcoal-light" data-testid={`apply-file-name-${slot.key}`}>{file.name}</span>
        <div className="flex shrink-0 items-center gap-2">
          <label className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gold-deep hover:underline">
            Replace
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => e.target.files[0] && onPick(e.target.files[0])} />
          </label>
          <button type="button" data-testid={`apply-file-remove-${slot.key}`} onClick={onClear} className="rounded-full p-1 text-charcoal-faint hover:bg-blush-100 hover:text-red-500" aria-label={`Remove ${slot.label}`}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    ) : (
      <label
        data-testid={`apply-file-pick-${slot.key}`}
        className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blush-300 bg-blush-50/50 px-4 py-6 text-sm text-charcoal-muted transition-colors hover:border-gold/50 hover:bg-blush-50"
      >
        <UploadCloud className="h-5 w-5 text-gold" strokeWidth={1.5} />
        Tap to upload — PDF, JPG, PNG, DOC up to 10 MB
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => e.target.files[0] && onPick(e.target.files[0])} />
      </label>
    )}
  </div>
);

const AckCheckbox = ({ checked, onChange, text, testid }) => (
  <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-blush-200 bg-white p-4 transition-colors hover:border-gold/40">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid}
      className="mt-1 h-5 w-5 shrink-0 rounded accent-[#C59B27]" />
    <span className="text-sm leading-relaxed text-charcoal-light">{text}</span>
  </label>
);

const Apply = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(() => {
    try {
      return { ...initialData, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") };
    } catch {
      return initialData;
    }
  });
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
  }, [data]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setListItem = (list, i, k, v) =>
    setData((d) => ({ ...d, [list]: d[list].map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const addListItem = (list, blank) => setData((d) => ({ ...d, [list]: [...d[list], blank] }));
  const removeListItem = (list, i) => setData((d) => ({ ...d, [list]: d[list].filter((_, j) => j !== i) }));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!data.legal_name.trim()) e.legal_name = "Full legal name is required";
      if (!data.address.trim()) e.address = "Address is required";
      if (!data.city.trim()) e.city = "City is required";
      if (!data.state.trim()) e.state = "State is required";
      if (!data.zip.trim()) e.zip = "ZIP code is required";
      if (!data.phone.trim()) e.phone = "Phone number is required";
      if (!/^\S+@\S+\.\S+$/.test(data.email)) e.email = "A valid email address is required";
      if (!data.preferred_contact) e.preferred_contact = "Please choose a contact method";
    }
    if (step === 1 && data.positions.length === 0) e.positions = "Select at least one position";
    if (step === 2) {
      const anyDay = Object.values(data.availability.week || {}).some((d) => d.am || d.pm);
      if (data.availability.types.length === 0 && data.availability.shifts.length === 0 && !anyDay)
        e.availability = "Select at least one availability option";
    }
    if (step === 3) {
      if (!data.service_areas.trim()) e.service_areas = "Tell us where you can work";
      if (!data.reliable_transportation) e.reliable_transportation = "Please answer";
      if (!data.is_18) e.is_18 = "Please answer";
      if (!data.work_authorized) e.work_authorized = "Please answer";
      if (!data.convicted) e.convicted = "Please answer";
      if (data.convicted === "Yes" && !data.conviction_details.trim()) e.conviction_details = "Please provide details";
    }
    if (step === 6) {
      const filled = data.references.filter((r) => r.name.trim() && r.phone.trim());
      if (filled.length < 2) e.references = "Please provide at least two references with a name and phone number";
    }
    if (step === 8) {
      if (!data.certify_true) e.certify_true = "This certification is required";
      if (!data.ack_authorization) e.ack_authorization = "This authorization is required";
      if (data.signature_name.trim().length < 3) e.signature_name = "Type your full legal name to sign";
      if (!data.signature_date) e.signature_date = "Date is required";
    }
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please complete the highlighted fields before continuing.");
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const back = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(data));
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
      await api.post("/applications", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
      });
      localStorage.removeItem(DRAFT_KEY);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="bg-blush-50 pb-24 pt-40">
        <div className="mx-auto max-w-2xl px-5 text-center sm:px-8">
          <CheckCircle2 className="mx-auto h-16 w-16 text-gold" strokeWidth={1.2} />
          <h1 data-testid="apply-success-heading" className="mt-6 font-serif text-3xl text-charcoal sm:text-4xl">
            Application Received
          </h1>
          <p data-testid="apply-success-message" className="mt-5 text-base leading-relaxed text-charcoal-muted">
            Thank you for applying to New Beginnings Healthcare Services. Your application has been
            successfully received. If your qualifications match one of our available opportunities,
            a member of our team will contact you regarding the next steps.
          </p>
          <Link to="/" className="btn-gold-outline mt-10">Back to Home</Link>
        </div>
      </main>
    );
  }

  const input = (k, props = {}) => (
    <input
      value={data[k]}
      onChange={(e) => set(k, e.target.value)}
      className={`field-input ${errors[k] ? "!border-red-400 !ring-2 !ring-red-200" : ""}`}
      {...inputProps(`apply-${k.replace(/_/g, "-")}-input`)}
      {...props}
    />
  );

  const stepBody = [
    /* 0 — Personal Information */
    <div className="grid gap-5 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field label="Full legal name" htmlFor="ap-name" required>{input("legal_name", { id: "ap-name", placeholder: "As it appears on your ID" })}</Field>
        <Err k="legal_name" errors={errors} />
      </div>
      <div className="sm:col-span-2">
        <Field label="Street address" htmlFor="ap-address" required>{input("address", { id: "ap-address", placeholder: "123 Main Street, Apt 4" })}</Field>
        <Err k="address" errors={errors} />
      </div>
      <div>
        <Field label="City" htmlFor="ap-city" required>{input("city", { id: "ap-city" })}</Field>
        <Err k="city" errors={errors} />
      </div>
      <div className="grid grid-cols-2 gap-5">
        <div>
          <Field label="State" htmlFor="ap-state" required>{input("state", { id: "ap-state" })}</Field>
          <Err k="state" errors={errors} />
        </div>
        <div>
          <Field label="ZIP code" htmlFor="ap-zip" required>{input("zip", { id: "ap-zip" })}</Field>
          <Err k="zip" errors={errors} />
        </div>
      </div>
      <div>
        <Field label="Phone number" htmlFor="ap-phone" required>{input("phone", { id: "ap-phone", type: "tel", placeholder: "(856) 555-0100" })}</Field>
        <Err k="phone" errors={errors} />
      </div>
      <div>
        <Field label="Email address" htmlFor="ap-email" required>{input("email", { id: "ap-email", type: "email", placeholder: "you@email.com" })}</Field>
        <Err k="email" errors={errors} />
      </div>
      <div className="sm:col-span-2">
        <span className="field-label">Preferred method of contact <span className="ml-1 text-gold">*</span></span>
        <Chips options={["Phone", "Email", "Text Message"]} values={data.preferred_contact ? [data.preferred_contact] : []}
          onChange={(v) => set("preferred_contact", v[v.length - 1] || "")} testid="apply-contact-method" />
        <Err k="preferred_contact" errors={errors} />
      </div>
    </div>,

    /* 1 — Position */
    <div className="grid gap-5">
      <div>
        <span className="field-label">Position(s) you are applying for — select all that apply <span className="ml-1 text-gold">*</span></span>
        <Chips options={POSITIONS} values={data.positions} onChange={(v) => set("positions", v)} testid="apply-position" />
        <Err k="positions" errors={errors} />
      </div>
      {data.positions.includes("Other") && (
        <Field label="Please specify" htmlFor="ap-other-position">
          {input("other_position", { id: "ap-other-position", placeholder: "Other position" })}
        </Field>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="How did you hear about this position?" htmlFor="ap-hear">
          {input("hear_about", { id: "ap-hear", placeholder: "e.g. Indeed, referral, our website" })}
        </Field>
        <Field label="Salary desired" htmlFor="ap-salary">
          {input("salary_desired", { id: "ap-salary", placeholder: "e.g. $18/hr or $45,000" })}
        </Field>
      </div>
      <Field label="On what date can you start working if you are hired?" htmlFor="ap-start">
        {input("start_date", { id: "ap-start", type: "date" })}
      </Field>
    </div>,

    /* 2 — Availability */
    <div className="grid gap-6">
      <div>
        <span className="field-label">Employment type <span className="ml-1 text-gold">*</span></span>
        <Chips options={AVAIL_TYPES} values={data.availability.types}
          onChange={(v) => set("availability", { ...data.availability, types: v })} testid="apply-avail-type" />
      </div>
      <div>
        <span className="field-label">Shifts you can work</span>
        <Chips options={AVAIL_SHIFTS} values={data.availability.shifts}
          onChange={(v) => set("availability", { ...data.availability, shifts: v })} testid="apply-avail-shift" />
      </div>
      <div>
        <span className="field-label">Weekly availability — tap the days/times you can work</span>
        <DayGrid week={data.availability.week}
          onChange={(w) => set("availability", { ...data.availability, week: w })} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <span className="field-label">If needed, are you available to work overtime?</span>
          <Chips options={["Yes", "No"]} values={data.availability.overtime ? [data.availability.overtime] : []}
            onChange={(v) => set("availability", { ...data.availability, overtime: v[v.length - 1] || "" })} testid="apply-overtime" />
        </div>
      </div>
      <Field label="Specific days and hours you are available" htmlFor="ap-avail-details">
        <textarea id="ap-avail-details" rows={3} value={data.availability.details}
          onChange={(e) => set("availability", { ...data.availability, details: e.target.value })}
          placeholder="e.g. Monday–Friday 8am–4pm, every other weekend..." {...inputProps("apply-availability-details-textarea")} />
      </Field>
      <Err k="availability" errors={errors} />
    </div>,

    /* 3 — Service Area & Eligibility */
    <div className="grid gap-5">
      <Field label="What cities, counties, or areas are you willing to work in?" htmlFor="ap-areas" required>
        <textarea id="ap-areas" rows={3} value={data.service_areas} onChange={(e) => set("service_areas", e.target.value)}
          placeholder="e.g. Cherry Hill, Camden County, Burlington County, willing to travel up to 25 miles..."
          className={`field-input ${errors.service_areas ? "!border-red-400 !ring-2 !ring-red-200" : ""}`} {...inputProps("apply-service-areas-textarea")} />
      </Field>
      <Err k="service_areas" errors={errors} />
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <span className="field-label">Do you have reliable transportation to and from work? <span className="ml-1 text-gold">*</span></span>
          <Chips options={["Yes", "No"]} values={data.reliable_transportation ? [data.reliable_transportation] : []}
            onChange={(v) => set("reliable_transportation", v[v.length - 1] || "")} testid="apply-transportation" />
          <Err k="reliable_transportation" errors={errors} />
        </div>
        <div>
          <span className="field-label">Do you have a valid driver's license? (if applicable)</span>
          <Chips options={["Yes", "No", "Not applicable"]} values={data.drivers_license ? [data.drivers_license] : []}
            onChange={(v) => set("drivers_license", v[v.length - 1] || "")} testid="apply-drivers-license" />
        </div>
      </div>
      <div className="grid gap-5 rounded-2xl border border-blush-200 bg-blush-50/50 p-5">
        <div>
          <span className="field-label">Are you 18 years of age or older? <span className="ml-1 text-gold">*</span></span>
          <Chips options={["Yes", "No"]} values={data.is_18 ? [data.is_18] : []}
            onChange={(v) => set("is_18", v[v.length - 1] || "")} testid="apply-is-18" />
          <Err k="is_18" errors={errors} />
        </div>
        <div>
          <span className="field-label">Are you a U.S. citizen or approved to work in the United States? <span className="ml-1 text-gold">*</span></span>
          <Chips options={["Yes", "No"]} values={data.work_authorized ? [data.work_authorized] : []}
            onChange={(v) => set("work_authorized", v[v.length - 1] || "")} testid="apply-work-authorized" />
          <Err k="work_authorized" errors={errors} />
        </div>
        <Field label="What document can you provide as proof of citizenship or legal status?" htmlFor="ap-proof">
          {input("proof_document", { id: "ap-proof", placeholder: "e.g. U.S. passport, birth certificate, work authorization card" })}
        </Field>
        <div>
          <span className="field-label">Have you ever been convicted of a criminal offense (felony or misdemeanor)? <span className="ml-1 text-gold">*</span></span>
          <Chips options={["Yes", "No"]} values={data.convicted ? [data.convicted] : []}
            onChange={(v) => set("convicted", v[v.length - 1] || "")} testid="apply-convicted" />
          <Err k="convicted" errors={errors} />
        </div>
        {data.convicted === "Yes" && (
          <div>
            <Field label="Please state the nature of the crime(s), when and where convicted, and disposition of the case" htmlFor="ap-conviction" required>
              <textarea id="ap-conviction" rows={3} value={data.conviction_details} onChange={(e) => set("conviction_details", e.target.value)}
                className={`field-input ${errors.conviction_details ? "!border-red-400 !ring-2 !ring-red-200" : ""}`} {...inputProps("apply-conviction-details-textarea")} />
            </Field>
            <Err k="conviction_details" errors={errors} />
          </div>
        )}
        <p className="text-xs leading-relaxed text-charcoal-faint">
          Note: No applicant will be denied employment solely on the grounds of conviction of a criminal offense.
          The date of the offense, the nature of the offense, including any significant details that affect the
          description of the event, the surrounding circumstances, and the relevance of the offense to the
          position(s) applied for may, however, be considered.
        </p>
      </div>
    </div>,

    /* 4 — Education, Employment & Military */
    <div className="grid gap-10">
      <div>
        <h3 className="font-serif text-xl text-charcoal">Education and Training</h3>
        <div className="mt-4 overflow-hidden rounded-2xl border border-blush-200">
          <div className="hidden grid-cols-[1.1fr_1.6fr_1fr_1.2fr] bg-blush-100/70 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-charcoal-muted sm:grid">
            <span className="px-4 py-2.5">Level</span><span className="px-4 py-2.5">School</span><span className="px-4 py-2.5">Year graduated</span><span className="px-4 py-2.5">Degree / Program</span>
          </div>
          {data.education.map((ed, i) => (
            <div key={ed.level} className="grid gap-3 border-t border-blush-100 p-4 sm:grid-cols-[1.1fr_1.6fr_1fr_1.2fr] sm:items-center sm:gap-0 sm:p-0">
              <span className="text-sm font-semibold text-charcoal sm:px-4 sm:py-3">{ed.level}</span>
              <span className="sm:px-2 sm:py-2">
                <input value={ed.school} onChange={(e) => setListItem("education", i, "school", e.target.value)} placeholder="School name" {...inputProps(`apply-education-school-${i}`)} />
              </span>
              <span className="sm:px-2 sm:py-2">
                <input value={ed.year} onChange={(e) => setListItem("education", i, "year", e.target.value)} placeholder="Year" {...inputProps(`apply-education-year-${i}`)} />
              </span>
              <span className="sm:px-2 sm:py-2">
                <input value={ed.degree} onChange={(e) => setListItem("education", i, "degree", e.target.value)} placeholder="Degree / diploma" {...inputProps(`apply-education-degree-${i}`)} />
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-charcoal">Previous Employment</h3>
        <p className="mt-1 text-sm text-charcoal-muted">Start with your most recent employer.</p>
        {data.employment.map((job, i) => (
          <div key={i} className="mt-4 grid gap-4 rounded-2xl border border-blush-200 bg-blush-50/50 p-5 sm:grid-cols-2">
            <Field label="Employer name" htmlFor={`em-employer-${i}`}>
              <input id={`em-employer-${i}`} value={job.employer} onChange={(e) => setListItem("employment", i, "employer", e.target.value)} {...inputProps(`apply-employment-employer-${i}`)} />
            </Field>
            <Field label="Job title" htmlFor={`em-title-${i}`}>
              <input id={`em-title-${i}`} value={job.job_title} onChange={(e) => setListItem("employment", i, "job_title", e.target.value)} {...inputProps(`apply-employment-title-${i}`)} />
            </Field>
            <Field label="Supervisor name" htmlFor={`em-supervisor-${i}`}>
              <input id={`em-supervisor-${i}`} value={job.supervisor} onChange={(e) => setListItem("employment", i, "supervisor", e.target.value)} {...inputProps(`apply-employment-supervisor-${i}`)} />
            </Field>
            <Field label="Employer telephone" htmlFor={`em-phone-${i}`}>
              <input id={`em-phone-${i}`} type="tel" value={job.employer_phone} onChange={(e) => setListItem("employment", i, "employer_phone", e.target.value)} {...inputProps(`apply-employment-phone-${i}`)} />
            </Field>
            <Field label="Employer address" htmlFor={`em-addr-${i}`}>
              <input id={`em-addr-${i}`} value={job.employer_address} onChange={(e) => setListItem("employment", i, "employer_address", e.target.value)} {...inputProps(`apply-employment-address-${i}`)} />
            </Field>
            <Field label="City, state and ZIP code" htmlFor={`em-csz-${i}`}>
              <input id={`em-csz-${i}`} value={job.employer_city_state_zip} onChange={(e) => setListItem("employment", i, "employer_city_state_zip", e.target.value)} {...inputProps(`apply-employment-citystatezip-${i}`)} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Employed from" htmlFor={`em-from-${i}`}>
                <input id={`em-from-${i}`} type="month" value={job.from} onChange={(e) => setListItem("employment", i, "from", e.target.value)} {...inputProps(`apply-employment-from-${i}`)} />
              </Field>
              <Field label="To" htmlFor={`em-to-${i}`}>
                <input id={`em-to-${i}`} type="month" value={job.to} onChange={(e) => setListItem("employment", i, "to", e.target.value)} {...inputProps(`apply-employment-to-${i}`)} />
              </Field>
            </div>
            <Field label="Reason for leaving" htmlFor={`em-reason-${i}`}>
              <input id={`em-reason-${i}`} value={job.reason_leaving} onChange={(e) => setListItem("employment", i, "reason_leaving", e.target.value)} {...inputProps(`apply-employment-reason-${i}`)} />
            </Field>
            {data.employment.length > 1 && (
              <button type="button" onClick={() => removeListItem("employment", i)} className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:underline sm:col-span-2">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
        ))}
        {data.employment.length < 5 && (
          <button type="button" data-testid="apply-add-employment" onClick={() => addListItem("employment", { employer: "", job_title: "", supervisor: "", employer_address: "", employer_city_state_zip: "", employer_phone: "", from: "", to: "", reason_leaving: "" })}
            className="mt-4 flex items-center gap-2 text-sm font-semibold text-gold-deep hover:underline">
            <Plus className="h-4 w-4" /> Add another employer
          </button>
        )}
      </div>

      <div>
        <h3 className="font-serif text-xl text-charcoal">Military Service <span className="ml-2 font-sans text-xs font-medium uppercase tracking-wide text-charcoal-faint">Optional</span></h3>
        <div className="mt-4 grid gap-4 rounded-2xl border border-blush-200 bg-blush-50/50 p-5 sm:grid-cols-2">
          <Field label="Branch of service" htmlFor="mi-branch">
            <input id="mi-branch" value={data.military.branch} onChange={(e) => set("military", { ...data.military, branch: e.target.value })} {...inputProps("apply-military-branch-input")} />
          </Field>
          <Field label="Rank when discharged" htmlFor="mi-rank">
            <input id="mi-rank" value={data.military.rank} onChange={(e) => set("military", { ...data.military, rank: e.target.value })} {...inputProps("apply-military-rank-input")} />
          </Field>
          <Field label="Years served" htmlFor="mi-years">
            <input id="mi-years" value={data.military.years} onChange={(e) => set("military", { ...data.military, years: e.target.value })} {...inputProps("apply-military-years-input")} />
          </Field>
          <Field label="Military skills relevant to this position" htmlFor="mi-skills">
            <input id="mi-skills" value={data.military.skills} onChange={(e) => set("military", { ...data.military, skills: e.target.value })} {...inputProps("apply-military-skills-input")} />
          </Field>
        </div>
      </div>
    </div>,

    /* 5 — Licenses, Insurance & Skills */
    <div className="grid gap-6">
      <p className="text-sm text-charcoal-muted">
        Add each license you currently hold (RN, LPN, HHA, NA), with its number, issuing authority or board, and expiration date.
      </p>
      {data.licenses.map((lic, i) => (
        <div key={i} className="grid gap-4 rounded-2xl border border-blush-200 bg-blush-50/50 p-5 sm:grid-cols-2">
          <Field label="Type of license held" htmlFor={`lic-type-${i}`}>
            <select id={`lic-type-${i}`} value={lic.type} onChange={(e) => setListItem("licenses", i, "type", e.target.value)} {...inputProps(`apply-license-type-${i}`)}>
              <option value="">Select type</option>
              {LICENSE_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="License number" htmlFor={`lic-num-${i}`}>
            <input id={`lic-num-${i}`} value={lic.number} onChange={(e) => setListItem("licenses", i, "number", e.target.value)} {...inputProps(`apply-license-number-${i}`)} />
          </Field>
          <Field label="License issuing authority or board" htmlFor={`lic-auth-${i}`}>
            <input id={`lic-auth-${i}`} value={lic.issuing_authority} onChange={(e) => setListItem("licenses", i, "issuing_authority", e.target.value)} placeholder="e.g. NJ Board of Nursing" {...inputProps(`apply-license-authority-${i}`)} />
          </Field>
          <Field label="License expiration" htmlFor={`lic-exp-${i}`}>
            <input id={`lic-exp-${i}`} type="date" value={lic.expiration} onChange={(e) => setListItem("licenses", i, "expiration", e.target.value)} {...inputProps(`apply-license-expiration-${i}`)} />
          </Field>
          {data.licenses.length > 1 && (
            <div className="flex items-end">
              <button type="button" onClick={() => removeListItem("licenses", i)} className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:underline">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          )}
        </div>
      ))}
      <button type="button" data-testid="apply-add-license" onClick={() => addListItem("licenses", { type: "", number: "", issuing_authority: "", expiration: "" })}
        className="flex items-center gap-2 text-sm font-semibold text-gold-deep hover:underline">
        <Plus className="h-4 w-4" /> Add another license
      </button>

      <div>
        <h3 className="font-serif text-xl text-charcoal">Malpractice Insurance <span className="ml-2 font-sans text-xs font-medium uppercase tracking-wide text-charcoal-faint">Where applicable</span></h3>
        <div className="mt-4 grid gap-4 rounded-2xl border border-blush-200 bg-blush-50/50 p-5 sm:grid-cols-2">
          <Field label="Policy number" htmlFor="mp-policy">
            {input("malpractice_policy", { id: "mp-policy" })}
          </Field>
          <Field label="Insurance carrier name" htmlFor="mp-carrier">
            {input("malpractice_carrier", { id: "mp-carrier" })}
          </Field>
          <div className="sm:col-span-2">
            <Field label="Carrier address" htmlFor="mp-address">
              {input("malpractice_carrier_address", { id: "mp-address" })}
            </Field>
          </div>
        </div>
      </div>

      <Field label="Job skills & qualifications — list the skills and qualifications you possess for this position" htmlFor="ap-skills">
        <textarea id="ap-skills" rows={4} value={data.skills} onChange={(e) => set("skills", e.target.value)}
          placeholder="e.g. wound care, medication reminders, Hoyer lift experience, dementia care, bilingual English/Spanish..."
          {...inputProps("apply-skills-textarea")} />
      </Field>
    </div>,

    /* 6 — References */
    <div className="grid gap-5">
      <p className="text-sm text-charcoal-muted">
        Please provide at least two professional references (supervisors or colleagues — not family members).
      </p>
      {data.references.map((ref, i) => (
        <div key={i} className="grid gap-4 rounded-2xl border border-blush-200 bg-blush-50/50 p-5 sm:grid-cols-2">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gold-deep sm:col-span-2">Reference {i + 1}{i < 2 ? " (required)" : " (optional)"}</p>
          <Field label="Full name" htmlFor={`ref-name-${i}`}>
            <input id={`ref-name-${i}`} value={ref.name} onChange={(e) => setListItem("references", i, "name", e.target.value)} {...inputProps(`apply-reference-name-${i}`)} />
          </Field>
          <Field label="Relationship" htmlFor={`ref-rel-${i}`}>
            <input id={`ref-rel-${i}`} value={ref.relationship} onChange={(e) => setListItem("references", i, "relationship", e.target.value)} placeholder="e.g. Former supervisor" {...inputProps(`apply-reference-relationship-${i}`)} />
          </Field>
          <Field label="Company / Organization" htmlFor={`ref-company-${i}`}>
            <input id={`ref-company-${i}`} value={ref.company} onChange={(e) => setListItem("references", i, "company", e.target.value)} {...inputProps(`apply-reference-company-${i}`)} />
          </Field>
          <Field label="Phone" htmlFor={`ref-phone-${i}`}>
            <input id={`ref-phone-${i}`} type="tel" value={ref.phone} onChange={(e) => setListItem("references", i, "phone", e.target.value)} {...inputProps(`apply-reference-phone-${i}`)} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Email (optional)" htmlFor={`ref-email-${i}`}>
              <input id={`ref-email-${i}`} type="email" value={ref.email} onChange={(e) => setListItem("references", i, "email", e.target.value)} {...inputProps(`apply-reference-email-${i}`)} />
            </Field>
          </div>
        </div>
      ))}
      <Err k="references" errors={errors} />
    </div>,

    /* 7 — Documents */
    <div className="grid gap-5">
      <p className="text-sm leading-relaxed text-charcoal-muted">
        Upload any of the following (all optional at this stage): your resume, professional
        license/certification, CPR/BLS card, or other employment credentials. Accepted formats:
        PDF, JPG, JPEG, PNG, DOC, DOCX — up to 10 MB each.
      </p>
      <div className="rounded-2xl border border-gold/30 bg-gold-soft/50 p-4 text-xs leading-relaxed text-charcoal-muted">
        Please do <strong className="text-charcoal">not</strong> upload Social Security cards, driver's
        licenses, passports, I-9 documents, tax forms, or banking information here. Those are only
        collected later through the secure onboarding portal if you are hired.
      </div>
      {DOC_SLOTS.map((slot) => (
        <FileSlot key={slot.key} slot={slot} file={files[slot.key]}
          onPick={(f) => setFiles((prev) => ({ ...prev, [slot.key]: f }))}
          onClear={() => setFiles((prev) => ({ ...prev, [slot.key]: null }))} />
      ))}
    </div>,

    /* 8 — Review & Sign */
    <div className="grid gap-6">
      <div className="rounded-2xl border border-blush-200 bg-blush-50/50 p-5 text-sm text-charcoal-light">
        <p className="font-semibold text-charcoal">{data.legal_name || "—"}</p>
        <p className="mt-1">{[data.address, data.city, data.state, data.zip].filter(Boolean).join(", ")}</p>
        <p className="mt-1">{data.phone} &middot; {data.email}</p>
        <p className="mt-2"><span className="font-semibold text-charcoal">Positions:</span> {data.positions.join(", ") || "—"}{data.other_position ? ` (${data.other_position})` : ""}</p>
        {data.salary_desired && <p className="mt-1"><span className="font-semibold text-charcoal">Salary desired:</span> {data.salary_desired}</p>}
        {data.start_date && <p className="mt-1"><span className="font-semibold text-charcoal">Can start:</span> {data.start_date}</p>}
        <p className="mt-1"><span className="font-semibold text-charcoal">Availability:</span> {[...data.availability.types, ...data.availability.shifts].join(", ") || "—"}</p>
        <p className="mt-1"><span className="font-semibold text-charcoal">Service areas:</span> {data.service_areas || "—"}</p>
        <p className="mt-1"><span className="font-semibold text-charcoal">Documents:</span> {Object.values(files).filter(Boolean).map((f) => f.name).join(", ") || "None attached"}</p>
        <p className="mt-3 text-xs text-charcoal-faint">Use Previous to go back and edit any section — your answers are saved automatically on this device.</p>
      </div>
      <div className="grid gap-3">
        <AckCheckbox testid="apply-ack-certify" checked={data.certify_true} onChange={(v) => set("certify_true", v)}
          text="I certify that the information provided in this employment application is true and complete to the best of my knowledge." />
        <Err k="certify_true" errors={errors} />
        <AckCheckbox testid="apply-ack-authorization" checked={data.ack_authorization} onChange={(v) => set("ack_authorization", v)}
          text="I hereby authorize New Beginnings Healthcare Services to request and receive, from all prior employers within one year of the date of this application, all pertinent information concerning my prior employment and its termination, including the reason for such termination." />
        <Err k="ack_authorization" errors={errors} />
      </div>
      <div className="grid gap-5 rounded-2xl border border-gold/30 bg-gold-soft/40 p-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <p className="text-sm font-semibold text-charcoal">Electronic Signature</p>
          <p className="mt-1 text-xs text-charcoal-muted">Type your full legal name below to sign this application electronically.</p>
        </div>
        <Field label="Full legal name (signature)" htmlFor="ap-sig" required>
          <input id="ap-sig" value={data.signature_name} onChange={(e) => set("signature_name", e.target.value)}
            placeholder="Type your full legal name"
            className={`field-input font-serif !text-lg italic ${errors.signature_name ? "!border-red-400 !ring-2 !ring-red-200" : ""}`}
            {...inputProps("apply-signature-input")} />
        </Field>
        <Field label="Date" htmlFor="ap-sig-date" required>
          <input id="ap-sig-date" type="date" value={data.signature_date} onChange={(e) => set("signature_date", e.target.value)} {...inputProps("apply-signature-date-input")} />
        </Field>
        <div className="sm:col-span-2"><Err k="signature_name" errors={errors} /></div>
      </div>
    </div>,
  ];

  return (
    <main className="bg-blush-50/60 pb-24 pt-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-center">
          <span className="overline-gold">Employment Application</span>
          <h1 className="mt-3 font-serif text-3xl font-medium text-charcoal sm:text-4xl">Apply to New Beginnings</h1>
          <p className="mt-2 text-sm text-charcoal-muted">
            Your progress is saved automatically on this device — you can safely leave and come back.
          </p>
          <p className="mx-auto mt-4 max-w-xl text-xs leading-relaxed text-charcoal-faint">
            New Beginnings Healthcare Services is an equal opportunity employer. This application will not be
            used for limiting or excluding any applicant from consideration for employment on a basis prohibited
            by local, state, or federal law. Should an applicant need reasonable accommodation in the application
            process, he or she should contact a company representative at (856) 545-7393.
          </p>
        </div>

        <div className="mt-10" data-testid="apply-progress">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-muted">
            <span>Step {step + 1} of {STEPS.length}</span>
            <span className="text-gold-deep">{STEPS[step]}</span>
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-blush-200">
            <div className="h-full rounded-full bg-gradient-to-r from-rosebtn to-gold transition-[width] duration-500"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
          <div className="mt-3 hidden justify-between sm:flex">
            {STEPS.map((s, i) => (
              <span key={s} className={`text-[0.6rem] uppercase tracking-wide ${i <= step ? "text-gold-deep" : "text-charcoal-faint/60"}`}>
                {i + 1}
              </span>
            ))}
          </div>
        </div>

        <div data-testid="apply-step-body" className="mt-8 rounded-[2rem] border border-blush-200 bg-white p-6 shadow-[0_10px_44px_rgba(31,41,55,0.06)] sm:p-10">
          <h2 className="font-serif text-2xl text-charcoal">{STEPS[step]}</h2>
          <div className="mt-7">{stepBody[step]}</div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <button type="button" onClick={back} disabled={step === 0 || submitting} data-testid="apply-previous-button"
            className="btn-gold-outline disabled:invisible">
            <ArrowLeft className="h-4 w-4" /> Previous
          </button>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next} data-testid="apply-next-button" className="btn-pink">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting} data-testid="apply-submit-button"
              className="btn-pink disabled:opacity-60">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {submitting ? `Submitting... ${progress}%` : "Submit Application"}
            </button>
          )}
        </div>
        {submitting && (
          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-blush-200" data-testid="apply-upload-progress">
            <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </main>
  );
};

export default Apply;
